import { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetCourseBySlugQuery,
  useGetCourseResultQuery,
} from "@/features/api/courseApi";

import {
  User,
  Award,
  CheckCircle2,
  History,
  Loader2,
  BarChart3,
  Trophy,
} from "lucide-react";

export default function CourseResult() {
const { slug } = useParams();
const navigate = useNavigate();

  /* ================= COURSE ================= */
  const { data: courseRes, isLoading: courseLoading } =
    useGetCourseBySlugQuery(slug, { skip: !slug });

  const course = courseRes?.course;

  /* ================= RESULT ================= */
  const {
    data: rawResult,
    isLoading: resultLoading,
    error,
  } = useGetCourseResultQuery(course?._id, {
    skip: !course?._id,
    refetchOnMountOrArgChange: true,
  });

  /* ================= NORMALIZE BACKEND ================= */
  const { results, user } = useMemo(() => {
    if (!rawResult) return { results: [], user: {} };

    // handle multiple backend shapes safely
    if (rawResult.results) {
      return {
        results: rawResult.results,
        user: rawResult.user || {},
      };
    }

    if (rawResult.data?.results) {
      return {
        results: rawResult.data.results,
        user: rawResult.data.user || {},
      };
    }

    return { results: [], user: {} };
  }, [rawResult]);

  /* ================= LOADING ================= */
  if (courseLoading || resultLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="animate-spin mr-2" /> Loading results...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        Failed to load results
      </div>
    );
  }

  if (!results.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        No attempts yet.
      </div>
    );
  }

  /* ================= CALCULATIONS ================= */
const attempts = results.length;
const percentages = results.map(r => Number(r.score || 0));

const bestPercentage = percentages.length ? Math.max(...percentages) : 0;

const avgPercentage = percentages.length
  ? Math.round(percentages.reduce((a,b)=>a+b,0)/percentages.length)
  : 0;

const latest = results[results.length - 1];
const latestPct = Number(latest?.score || 0);

const passed = latestPct >= 30;

  const handleRetake = () =>
navigate(`/courses/${course?.slug}/learn`);
  /* ================= UI ================= */
  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-5xl mx-auto space-y-8">

        <div className="text-center">
        <h1 className="text-3xl font-bold">{course?.title}</h1>
          <p className="text-gray-500">Course Result Summary</p>
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          <Stat icon={Trophy} label="Best %" value={`${bestPercentage}%`} />
          <Stat icon={BarChart3} label="Average %" value={`${avgPercentage}%`} />
          <Stat icon={History} label="Attempts" value={attempts} />
          <Stat icon={Award} label="Latest %" value={`${latestPct}%`} />
        </div>

        <Section title="Student Information" icon={User}>
          <Info label="Name" value={user.name || "—"} />
          <Info label="Email" value={user.email || "—"} />
        </Section>

        <Section title="Latest Attempt" icon={Award}>

          <Info label="Percentage" value={`${latestPct}%`} />

          <Info
            label="Status"
            value={
              <span
                className={
                  passed
                    ? "text-green-600 font-semibold"
                    : "text-red-600 font-semibold"
                }
              >
                {passed ? "Passed" : "Failed"}
              </span>
            }
          />

          <button
            onClick={handleRetake}
            className="mt-4 flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded"
          >
            <CheckCircle2 size={16} /> Retake Course
          </button>
        </Section>

        <Section title="Attempt History" icon={History}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th>#</th>
                <th>Score</th>
                <th>%</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {results.map((r, i) => {
                
                const total =
                r.totalQuestions ||
                r.total ||
                r.questionsCount ||
                100;
                
                const pct = Number(r.score || 0);

                  return (
                    <tr key={i} className="border-b text-center">
                    <td>{i + 1}</td>
                    <td>{r.score}/{total}</td>
                    <td>{pct}%</td>
                    <td className={pct >= 30 ? "text-green-600" : "text-red-600"}>
                      {pct >= 30 ? "Passed" : "Failed"}
                    </td>
                  </tr>
                  );
              })}
            </tbody>
          </table>
        </Section>
      </div>
    </div>
  );
}

/* ================= HELPERS ================= */

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-lg shadow p-4 text-center">
      <Icon className="mx-auto text-blue-600 mb-2" />
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white shadow rounded-lg p-6 space-y-4">
      <h2 className="flex items-center gap-2 text-xl font-semibold">
        <Icon size={20} className="text-blue-600" />
        {title}
      </h2>
      <div className="grid sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <p>
      <strong>{label}:</strong> {value}
    </p>
  );
}